import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { captureException } from '@sentry/node';

import { doRequest } from '@r1-frontend/do-request/index';
import { IVtbPayQrCode } from '@r1-frontend/api-domru/api-pay/v1/vtb-pay/qr-core/dto/IVtbPayQrCode';
import { getQrLink } from '@r1-frontend/api-domru/api-pay/v1/vtb-pay/qr-core/index';

import { BaseButton, btnSizes, btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import { RubleSymbol } from '@r1-frontend/ui-react/experimental/purchase/cost/Cost/RubleSymbol';

import ResultPopup, { defaultPopupState } from '~/src/features/payments-abn/components/PopupResult';
import { selectCardPaySum } from '~/src/features/payments-abn/components/SeasonTicketPayment/store/selectors/selectPaymentSum';
import { pushDataLayer } from '~/src/features/payments-abn/helpers/pushDataLayer';
import { setPaymentQrLink } from '~/src/features/payments-abn/store/actionCreators';
import { TState } from '~/src/store';

import { PayButton } from './styled';

interface IProps {
    providerId: number,
    userAgreement?: string,
    isDisabled: boolean,
    isLoadingQrData: boolean,
    setIsLoadingQrData: Dispatch<SetStateAction<boolean>>,
    onPayHandler: (reqAction: () => Promise<void>) => void,
}

const MobileSbp = ({
    providerId,
    userAgreement = '',
    isDisabled,
    isLoadingQrData,
    setIsLoadingQrData,
    onPayHandler,
}: IProps): JSX.Element => {
    const dispatch = useDispatch();
    const {
        auth: { isAuth },
        payment: { paymentData: { paymentSum }, sbpData: { qrLink } },
    } = useSelector((state: TState) => state);

    const cardPaymentSum = useSelector(selectCardPaySum);

    const [popupState, setPopupState] = useState(defaultPopupState);

    const buttonText = useMemo(() => paymentSum ? `Оплатить ${paymentSum} ${RubleSymbol}` : 'Оплатить', [paymentSum]);

    const fetchQrLink = useCallback(async() => {
        if (popupState.isOpen) {
            setPopupState(defaultPopupState);
        }
        setIsLoadingQrData(true);

        try {
            const _link = await doRequest.apiPay<IVtbPayQrCode>(
                getQrLink({ providerId, userAgreement, paySum: cardPaymentSum }),
            );

            if (_link.isSuccess) {
                dispatch(setPaymentQrLink(_link.payload.data.payload));
                pushDataLayer('click on show qrcode', 'succes qrcode');
            } else {
                pushDataLayer('click on show qrcode', 'error qrcode');
            }

            return _link;
        } catch (error) {
            captureException(error);
        } finally {
            setIsLoadingQrData(false);
        }
    }, [providerId, cardPaymentSum, popupState.isOpen, setIsLoadingQrData, userAgreement, dispatch]);

    const onFetchQrLinkHandler = useCallback(async() => {
        const qrLinkResult = await fetchQrLink();

        if (!qrLinkResult?.isSuccess) {
            setPopupState({
                isOpen: true,
                title: 'Не удалось получить ссылку на оплату',
                actions: <BaseButton
                    type={btnTypes.ACCENT}
                    size={btnSizes.LARGE}
                    text={'Попробовать еще раз'}
                    onClick={fetchQrLink}
                />,
            });
        }
    }, [fetchQrLink]);

    return (
        <>
            <PayButton
                type={btnTypes.ACCENT}
                size={btnSizes.LARGE}
                text={qrLink ? 'Подтвердить' : buttonText}
                onClick={qrLink ? () => ({}) : () => onPayHandler(onFetchQrLinkHandler)}
                isLoading={isLoadingQrData}
                disabled={isDisabled}
                href={qrLink}
                target='_blank'
                rel='noopener noreferrer'
                isAuth={isAuth}
            />
            <ResultPopup {...popupState} onClose={() => setPopupState(defaultPopupState)}/>
        </>
    );
};

export default MobileSbp;
